// src/Recommendations.js

import { useState, useEffect } from 'react';

const API_KEY = 'a1e72fd93ed59f56e6332813b9f8dcae';

export default function Recommendations({ item, onItemSelect }) {
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!item) return;

    const fetchRecommendations = async () => {
      setIsLoading(true);
      setShowAll(false); // Reset view when item changes
      try {
        const type = item.media_type === 'movie' ? 'movie' : 'tv';
        const res = await fetch(
          `https://api.themoviedb.org/3/${type}/${item.id}/recommendations?api_key=${API_KEY}&page=1`
        );
        const data = await res.json();
        
        // If no recommendations, try similar content
        if (!data.results || data.results.length === 0) {
          const similarRes = await fetch(
            `https://api.themoviedb.org/3/${type}/${item.id}/similar?api_key=${API_KEY}&page=1`
          );
          const similarData = await similarRes.json();
          setRecommendations(similarData.results?.slice(0, 12) || []);
        } else {
          setRecommendations(data.results.slice(0, 12));
        }
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        setRecommendations([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendations();
  }, [item?.id, item?.media_type]); // Only depend on item.id to ensure it updates when modal item changes

  const handleItemClick = (recommendedItem) => {
    // Add media_type to the item if it doesn't exist
    const itemWithMediaType = {
      ...recommendedItem,
      media_type: item.media_type // Inherit from parent item
    };
    onItemSelect(itemWithMediaType);
  };

  if (isLoading) {
    return (
      <div className="mt-8">
        <h3 className="text-xl font-bold text-white mb-4">Recommendations</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="bg-gray-700 rounded-lg aspect-[2/3] w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!recommendations.length) {
    return null;
  }

  // Show only 6 items initially, or all 12 if showAll is true
  const displayedRecommendations = showAll ? recommendations : recommendations.slice(0, 6);
  const hasMoreItems = recommendations.length > 6;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">
          {item.media_type === 'movie' ? 'Similar Movies' : 'Similar Shows'}
        </h3>
        {hasMoreItems && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors duration-200"
          >
            {showAll ? 'Show Less' : 'View All'}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {displayedRecommendations.map((rec) => (
          <div
            key={rec.id}
            className="group cursor-pointer transition-transform duration-200 hover:scale-105"
            onClick={() => handleItemClick(rec)}
          >
            <div className="relative overflow-hidden rounded-lg bg-gray-800">
              {rec.poster_path ? (
                <img
                  src={`https://image.tmdb.org/t/p/w342${rec.poster_path}`}
                  alt={rec.title || rec.name}
                  className="w-full h-auto aspect-[2/3] object-cover group-hover:opacity-80 transition-opacity duration-200"
                  loading="lazy"
                />
              ) : (
                <div className="w-full aspect-[2/3] bg-gray-700 flex items-center justify-center">
                  <span className="text-gray-400 text-xs text-center px-2">
                    No Image
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200"></div>
            </div>
            <div className="mt-2">
              <h4 className="text-white text-sm font-medium line-clamp-2 group-hover:text-blue-400 transition-colors duration-200">
                {rec.title || rec.name}
              </h4>
              <div className="flex items-center justify-between mt-1">
                <span className="text-gray-400 text-xs">
                  {rec.release_date?.split('-')[0] || rec.first_air_date?.split('-')[0]}
                </span>
                {rec.vote_average > 0 && (
                  <div className="flex items-center">
                    <span className="text-yellow-400 text-xs">★</span>
                    <span className="text-gray-300 text-xs ml-1">
                      {rec.vote_average.toFixed(1)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}